import React, { useState, useEffect, useRef } from 'react';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import Copyright from '../../internals/components/Copyright';
import CustomDateRangePicker from './CustomDateRangePicker';
import Top3 from './Top3';
import FollowUs from './FollowUs';
import UserCard from './UserCard';
import { Divider } from '@mui/material';
import MediaCarousel from './MediaCarousel';
import ScrollableMenuDashboard from './ScrollableMenuDashboard';
import ScrollableMenuGames from '../../MobileGames/components/ScrollableMenuGames';
import Banner from '../../internals/components/Banner';
import { useContent } from '../../../../context/ContentContext';
import format from 'date-fns/format';
import { Card, CardContent, Typography } from '@mui/material';
import subYears from 'date-fns/subYears';
import EventListDisplay from './EventListDisplay';


export default function MainGrid() {
  const [dateRange, setDateRange] = React.useState({ startDate: null, endDate: null });
  const { fetchSaldoData, saldoData, fetchTopAgentData, topAgentData } = useContent();

  const isFirstRender = useRef(true);

  const formatCurrency = (value) => {
    return `Rp. ${new Intl.NumberFormat('id-ID').format(value || 0)}`;
  };

  const cards = [
    {
      title: 'Total Saldo Aktif',
      value: formatCurrency(saldoData?.active_saldo),
      trend: 'up',
    },
    {
      title: 'Total Saldo Ditarik',
      value: formatCurrency(saldoData?.penarikan),
      trend: 'down',
    },
    {
      title: 'Total Pendapatan',
      value: formatCurrency(saldoData?.pendapatan),
      trend: 'neutral',
    },
  ];

  const handleDateChange = ({ startDate, endDate }) => {
    // Ensure both startDate and endDate are defined
    if (!startDate || !endDate) {
      console.error("Start date and end date are required.");
      return;
    }

    // Ensure the dates are valid before formatting
    try {
      const formattedStartDate = format(new Date(startDate), 'yyyy-MM-dd');
      const formattedEndDate = format(new Date(endDate), 'yyyy-MM-dd');

      // Check if the formatting was successful
      if (isNaN(new Date(formattedStartDate).getTime()) || isNaN(new Date(formattedEndDate).getTime())) {
        console.error("Invalid date format.");
        return;
      }

      setDateRange({ startDate: formattedStartDate, endDate: formattedEndDate });

      // Pass the formatted dates to the functions
      fetchSaldoData(formattedStartDate, formattedEndDate);
      fetchTopAgentData(formattedStartDate, formattedEndDate);

    } catch (error) {
      console.error("Error formatting dates:", error);
    }
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false; // Prevents the effect from running on the first render
      const defaultStartDate = subYears(new Date(), 4); // 4 years ago
      const defaultEndDate = new Date(); // Today

      handleDateChange({
        startDate: format(defaultStartDate, 'yyyy-MM-dd'),
        endDate: format(defaultEndDate, 'yyyy-MM-dd'),
      });
    }
  }, []);

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      fetchSaldoData(dateRange.startDate, dateRange.endDate);
      fetchTopAgentData(dateRange.startDate, dateRange.endDate);
    }
  }, [dateRange]);

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      {/* cards */}
      <UserCard />
      <Grid
        container
        spacing={1}
        columns={12}
        sx={{ mb: (theme) => theme.spacing(2) }}
      >
        <Grid size={{ xs: 12, lg: 12 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            {/* Pass the handleDateChange function to CustomDateRangePicker */}
            <CustomDateRangePicker onDateChange={handleDateChange} />
          </Box>
        </Grid>
        {/* Mobile horizontal scroll container */}
        <Grid
          item
          xs={12}
          sx={{
            display: { xs: 'block', sm: 'none' }, // Only show on mobile
            overflowX: 'auto', // Enables horizontal scrolling
            whiteSpace: 'nowrap', // Prevent wrapping of child elements
          }}
        >
          <Grid
            container
            spacing={1}
            columns={12}
            sx={{
              display: 'flex',
              flexWrap: 'nowrap', // Prevent wrapping of child items
            }}
          >
            {cards.map((card, index) => (
              <Grid key={index} item sx={{ minWidth: '300px' }}>
                <Card
                  sx={{
                    boxShadow: 3,
                    borderRadius: 3,
                    p: 2,
                    background: 'linear-gradient(135deg, #e3f2fd, #bbdefb)',
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 2,
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1565c0' }}>
                        {card.title}
                      </Typography>
                      {card.trend === 'up' && (
                        <Typography sx={{ color: 'green', fontWeight: 'bold' }}>⬆️</Typography>
                      )}
                      {card.trend === 'down' && (
                        <Typography sx={{ color: 'red', fontWeight: 'bold' }}>⬇️</Typography>
                      )}
                      {card.trend === 'neutral' && (
                        <Typography sx={{ color: 'gray', fontWeight: 'bold' }}>—</Typography>
                      )}
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
                      {card.value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Desktop view */}
        <Grid
          item
          xs={12}
          sx={{ display: { xs: 'none', sm: 'block' } }} // Only show on desktop
        >
          <Grid
            container
            spacing={2}
            columns={12}
            sx={{
              display: 'flex',
              justifyContent: 'space-between', // Distribute items with space between
            }}
          >
            {cards.map((card, index) => (
              <Grid
                key={index}
                item
                lg={3}
                sx={{
                  flex: '1',
                  minWidth: '32.33%', // Ensure cards have minimum width on desktop
                }}
              >
                <Card
                  sx={{
                    boxShadow: 3,
                    borderRadius: 3,
                    p: 2,
                    background: 'linear-gradient(135deg, #e3f2fd, #bbdefb)',
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 2,
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1565c0' }}>
                        {card.title}
                      </Typography>
                      {card.trend === 'up' && (
                        <Typography sx={{ color: 'green', fontWeight: 'bold' }}>⬆️</Typography>
                      )}
                      {card.trend === 'down' && (
                        <Typography sx={{ color: 'red', fontWeight: 'bold' }}>⬇️</Typography>
                      )}
                      {card.trend === 'neutral' && (
                        <Typography sx={{ color: 'gray', fontWeight: 'bold' }}>—</Typography>
                      )}
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
                      {card.value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={1} columns={12}>
        <Grid container spacing={1} columns={12} size={{ xs: 12, lg: 12 }}>
          <Grid size={{ xs: 12, lg: 12 }} sx={{ marginBottom: 1 }}>
            <Divider>HALL OF FAME</Divider>
            <Top3 dateRange={dateRange} topAgentData={topAgentData} />
          </Grid>
        </Grid>

        <Grid
          container
          spacing={1}
          columns={12}
          sx={{
            overflowX: 'auto', // Enable horizontal scrolling for the Grid container
            whiteSpace: 'nowrap', // Prevent child elements from wrapping
            '&::-webkit-scrollbar': {
              height: 200, // Custom scrollbar height
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#888', // Custom scrollbar color
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#555', // Darker on hover
            },
          }}
        >
          <Grid item xs={12} lg={12} sx={{ minWidth: '300px' }}>
            <Divider textAlign="right">SHORTCUTS MENU</Divider>
            <ScrollableMenuDashboard />
          </Grid>
          <Grid size={{ xs: 12, lg: 12 }}>
            <Divider textAlign="right" sx={{ mb: 1 }}>DAFTAR EVENT</Divider>
            <EventListDisplay />
          </Grid>
          <Grid size={{ xs: 12, lg: 12 }}>
            <Divider textAlign="right" sx={{ mb: 1 }}>INFORMATION</Divider>
            <MediaCarousel />
          </Grid>
          <Grid size={{ xs: 12, lg: 12 }}>
            <Divider>REACH US</Divider>
            <FollowUs />
          </Grid>
          <Grid item xs={12} lg={12} sx={{ Width: '100%' }}>
            <Divider textAlign="right">GAMES CATEGORY</Divider>
            <Banner
              image="images/bannerGames.png"
            />
            <ScrollableMenuGames />
          </Grid>
        </Grid>
      </Grid>
      <Copyright sx={{ my: 1, marginBottom: 5 }} />
    </Box>
  );
}
